// Comparison site: drop two heatmap PNGs, get a similarity percentage.
// Everything runs locally — the PNGs never leave the browser.

import {
    compareImages,
    loadImageToImageData,
    luminanceMasked,
} from "./imageSimilarity.ts";

// --- Helpers ---

function el<T extends HTMLElement>(id: string): T {
    return document.getElementById(id) as T;
}

const fmtPct = (x: number): string => `${(x * 100).toFixed(2)}%`;

// --- Panel state ---

interface Panel {
    el: HTMLElement;
    input: HTMLInputElement;
    prompt: HTMLElement;
    img: HTMLImageElement;
    errorEl: HTMLElement;
    luminance: Float32Array | null;
    version: number | null;
    url: string | null;
}

function setup(prefix: string): Panel {
    const p: Panel = {
        el: el(`panel${prefix}`),
        input: el<HTMLInputElement>(`file${prefix}`),
        prompt: el(`prompt${prefix}`),
        img: el<HTMLImageElement>(`img${prefix}`),
        errorEl: el(`error${prefix}`),
        luminance: null,
        version: null,
        url: null,
    };

    const handle = async (file: File) => {
        if (p.url) { URL.revokeObjectURL(p.url); p.url = null; }

        try {
            const { imageData, metadata } = await loadImageToImageData(file);
            p.luminance = luminanceMasked(imageData);
            p.version = metadata.version;
            p.url = URL.createObjectURL(file);
            p.img.src = p.url;
            p.img.hidden = false;
            p.el.classList.add("loaded");
            p.el.classList.remove("error");
            p.errorEl.hidden = true;
        } catch (err) {
            p.luminance = null;
            p.el.classList.remove("loaded");
            p.el.classList.add("error");
            p.img.hidden = true;
            p.img.removeAttribute("src");
            p.errorEl.textContent =
                err instanceof Error ? err.message : "Could not load image";
            p.errorEl.hidden = false;
        }
        update();
    };

    // Click to open file picker.
    p.el.addEventListener("click", () => p.input.click());
    p.el.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            p.input.click();
        }
    });
    p.input.addEventListener("change", () => {
        const file = p.input.files?.[0];
        if (file) handle(file);
    });

    // Drag & drop.
    p.el.addEventListener("dragover", (e) => {
        e.preventDefault();
        p.el.classList.add("drag");
    });
    p.el.addEventListener("dragleave", () => p.el.classList.remove("drag"));
    p.el.addEventListener("drop", (e) => {
        e.preventDefault();
        p.el.classList.remove("drag");
        const file = e.dataTransfer?.files?.[0];
        if (file) handle(file);
    });

    return p;
}

// --- Wiring ---

const panelA = setup("A");
const panelB = setup("B");
const scoreEl = el("score");
const scoreNum = el("scoreNum");
const versionMismatchEl = el("versionMismatch");
const shareBtn = el<HTMLButtonElement>("shareBtn");

let lastScore = 0;

function update() {
    const a = panelA.luminance;
    const b = panelB.luminance;
    const va = panelA.version;
    const vb = panelB.version;

    if (a && b) {
        if (va !== vb) {
            scoreEl.hidden = true;
            versionMismatchEl.hidden = false;
            shareBtn.hidden = true;
        } else {
            const r = compareImages(a, b);
            lastScore = r.cosine;
            scoreNum.textContent = fmtPct(r.cosine);
            scoreEl.hidden = false;
            versionMismatchEl.hidden = true;
            shareBtn.hidden = false;
            // Re-trigger animation.
            scoreEl.classList.remove("pop");
            void scoreEl.offsetHeight;
            scoreEl.classList.add("pop");
        }
    } else {
        scoreEl.hidden = true;
        versionMismatchEl.hidden = true;
        shareBtn.hidden = true;
    }
}

shareBtn.addEventListener("click", () => {
    const imgA = panelA.img;
    const imgB = panelB.img;
    if (!imgA.complete || !imgB.complete) return;

    // Each plot is rendered at half its native size and placed side by side
    // with no gap, so the two images fill the upper area completely.
    const plotW = 540;
    const plotH = 540;
    const labelH = 160;
    const totalW = plotW * 2;
    const totalH = plotH + labelH;

    const canvas = document.createElement("canvas");
    canvas.width = totalW;
    canvas.height = totalH;
    const ctx = canvas.getContext("2d")!;

    // White background.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, totalW, totalH);

    // Draw the plot area from each image, cropped from the full PNG.
    ctx.drawImage(imgA, 0, 0, 1080, 1080, 0, 0, plotW, plotH);
    ctx.drawImage(imgB, 0, 0, 1080, 1080, plotW, 0, plotW, plotH);

    // Score label.
    ctx.fillStyle = "#333333";
    ctx.font = "bold 120px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(fmtPct(lastScore), totalW / 2, plotH + labelH / 2);

    // Subtle attribution.
    ctx.fillStyle = "#999999";
    ctx.font = "18px system-ui, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText("bhh.simpct.com", totalW - 12, totalH - 12);

    canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "heatmap-comparison.jpg";
        a.click();
        URL.revokeObjectURL(url);
    }, "image/jpeg", 0.92);
});

// --- Debug panel (theme toggle) ---

const debugEl = el("debug");
const debugToggle = el("debugToggle");
const debugPanel = el("debugPanel");
const themeSelect = el<HTMLSelectElement>("themeSelect");

// Show debug toggle when ?debug is in the URL.
if (window.location.search.includes("debug")) {
    debugEl.hidden = false;
}

debugToggle.addEventListener("click", () => {
    debugPanel.hidden = !debugPanel.hidden;
});

themeSelect.addEventListener("change", () => {
    const v = themeSelect.value;
    if (v === "") {
        document.documentElement.removeAttribute("data-theme");
    } else {
        document.documentElement.setAttribute("data-theme", v);
    }
});
